/**
 * Global TypeScript types — Play For Goals CRM
 * Derived from Prisma schema. All module-specific types live in src/pages/<module>/types.ts
 */

export enum UserRole {
  ADMIN = 'admin',
  CLUB = 'club',
  NGO = 'ngo',
  PLAYER = 'player',
  PUBLIC = 'public',
}

export type UserStatus = 'active' | 'suspended' | 'pending' | 'rejected';

export type MatchStatus = 'upcoming' | 'live' | 'completed' | 'cancelled';

export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export type DonationStatus = 'pledged' | 'funded' | 'received';

export type CompetitionType = 'LEAGUE' | 'CUP' | 'FRIENDLY' | 'TOURNAMENT' | 'OTHER';

export type CauseArea = 'EDUCATION' | 'HEALTH' | 'ENVIRONMENT' | 'POVERTY' | 'SPORTS' | 'OTHER';

export interface User {
  id: string;
  email: string;
  fullName?: string;
  name?: string;           // display alias
  displayName?: string;    // display alias
  phoneNumber?: string;
  emailVerified: boolean;
  role: UserRole;
  status?: UserStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Club {
  id: string;
  userId: string;
  name: string;
  logoUrl?: string;
  country?: string;
  city?: string;
  league?: string;
  bio?: string;
  contactInfo?: { email?: string; phone?: string };
  description?: string;
  ngoId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Ngo {
  id: string;
  userId: string;
  name: string;
  registrationNumber?: string;
  registrationCountry?: string;
  websiteUrl?: string;
  causeArea?: CauseArea;
  region?: string;
  description?: string;
  logoUrl?: string;
  coverPhotoUrl?: string;
  contactInfo?: { email?: string; phone?: string };
  fundingGoal?: number;
  impactStatistics?: { beneficiariesReached?: number; projectsCompleted?: number };
  profileCompletionPct: number;
  isPublished: boolean;
  certificateUrl?: string;
  proofOfCharityUrl?: string;
  annualReportUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Match {
  id: string;
  clubId: string;
  clubName?: string;
  opponentName: string;
  date: string;
  venue?: string;
  competitionType?: CompetitionType;
  scoreHome: number;
  scoreAway: number;
  goalsScored: number;
  status: MatchStatus;
  verificationStatus: VerificationStatus;
  donationTargetPerGoal?: number;
  verifiedAt?: string;
  verifiedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pledge {
  id: string;
  matchId: string;
  matchLabel?: string;
  ngoId: string;
  ngoName?: string;
  pledgePerGoal: number;
  totalGoals: number;
  totalDonation: number;
  status: DonationStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformStats {
  totalMatches: number;
  totalGoals: number;
  totalDonations: number;
  totalClubs: number;
  totalNgos: number;
  totalUsers: number;
  activeMatches: number;
  pendingVerifications: number;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export type Nullable<T>  = T | null;
export type Optional<T>  = T | undefined;
export type Maybe<T>     = T | null | undefined;
